import React from 'react';
import { Layout } from '../components/layout';
import { useSEO } from '../hooks/useSEO';

export const PrivacyPolicy: React.FC = () => {
  useSEO({
    title: 'Privacy Policy',
    description: 'Privacy Policy for ComponentScale — learn how we handle your data.',
  });

  return (
    <Layout>
      <div className="mx-auto max-w-3xl space-y-8 text-fg">
        <div>
          <h1 className="text-3xl font-semibold tracking-tightest text-fg">Privacy Policy</h1>
          <p className="mt-2 text-sm text-muted">Last updated: February 2026</p>
        </div>

        <Section title="Overview">
          <p>
            ComponentScale ("we", "us", or "our") operates the website componentscale.com. This page
            informs you of our policies regarding the collection, use, and disclosure of information
            when you use our service.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>
            We do not require you to create an account or provide personal information to use
            ComponentScale. The only data collected is anonymous usage analytics described below.
          </p>
        </Section>

        <Section title="Analytics">
          <p>
            We use Google Analytics 4 to understand how visitors interact with the site. Google
            Analytics collects information such as your IP address, browser type, pages visited, and
            time spent on pages. This data is aggregated and anonymised — we cannot identify
            individual users from it.
          </p>
          <p className="mt-3">
            You can opt out of Google Analytics tracking by installing the{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-ink hover:underline"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
            .
          </p>
        </Section>

        <Section title="Cookies and Local Storage">
          <p>
            ComponentScale uses browser local storage (not tracking cookies) to remember your
            preferences such as selected theme (light/dark mode), unit system (metric/imperial), and
            your current comparison selection. This data stays on your device and is never sent to
            our servers.
          </p>
        </Section>

        <Section title="Third-Party Links">
          <p>
            Our site contains links to third-party websites, including retailer product pages and
            affiliate links (see our{' '}
            <a href="/amazon-disclosure" className="text-accent-ink hover:underline">
              Amazon Disclosure
            </a>
            ). We are not responsible for the privacy practices of those sites. We encourage you to
            review the privacy policies of any third-party sites you visit.
          </p>
        </Section>

        <Section title="Data Sharing">
          <p>
            We do not sell, trade, or otherwise transfer your information to outside parties. The
            only data shared is the anonymous analytics data processed by Google Analytics under
            their own{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-ink hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </Section>

        <Section title="Children's Privacy">
          <p>
            ComponentScale is not directed at children under the age of 13 and we do not knowingly
            collect personal information from children.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with an updated revision date. Continued use of the site after changes constitutes
            acceptance of the updated policy.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            If you have any questions about this Privacy Policy, please open an issue on our{' '}
            <a
              href="https://github.com/anthropics/claude-code/issues"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-ink hover:underline"
            >
              GitHub repository
            </a>
            .
          </p>
        </Section>
      </div>
    </Layout>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-xl font-semibold text-fg">{title}</h2>
    <div className="text-sm leading-relaxed">{children}</div>
  </section>
);
